import { EditorPanelItem, EditorTool } from '@editor/interfaces/editor'
import { InspectionItem } from '@editor/interfaces/inspection'
import { InspectionGroup, InspectionGroupable } from '@editor/interfaces/inspectionGroup'
import { isDrawing } from '@editor/services/Drawing'
import { isInspectionGroup } from '@editor/services/InspectionGroup'
import { isInspectionItem } from '@editor/services/InspectionSheet'
import { deriveShapeType, isShape } from '@editor/services/Shape'
import { difference, omit } from 'lodash'

export const getElementId = (it: EditorPanelItem['item']): string | null => {
  if (it && 'id' in it) return it.id
  if (isInspectionItem(it)) return it.inspection_item_id!
  if (it && 'shape_id' in it) return it.shape_id
  if (it && 'region_id' in it) return it.region_id
  if (isInspectionGroup(it)) return it.group_id
  if (it && isDrawing(it)) return it.drawing_parameters.uuid
  return null
}

/**
 * Get the id of the item
 * @param it The item
 * @returns
 */
export const getId = (it: EditorPanelItem): string => {
  if (it.id) return it.id
  const elementId = getElementId(it.item)
  if (elementId) return elementId
  return it.key
}
/**
 * Collect IDs for deletion
 *
 * @param it The item to be deleted
 * @returns
 */
export const getIdsForDeletion = (it: EditorPanelItem): string[] => {
  let ids: string[] = []

  // If this is a virtual container setup to be deleted,
  // run the delete with it's children instead.
  if (it.isVirtualContainer && it.onDelete === true && it.children) {
    ids = ids.concat(...it.children.map(getIdsForDeletion).flat())
  }
  // Custom provided list of IDs to be deleted
  else if (Array.isArray(it.onDelete)) {
    ids = ids.concat(it.onDelete)
  }
  // Delete itself
  else {
    ids.push(getId(it))
  }

  return ids
}

/**
 * Check if the item or any of its children is or has working items.
 *
 * @param item The item to check
 * @returns
 */
export const checkIsWorking = (item: EditorPanelItem): boolean => {
  if (item.isWorking) return true
  if (item.children) {
    return item.children.some((child) => checkIsWorking(child))
  }
  return false
}

/**
 * Check if the current item is selectable based on the provided configuration.
 *
 * @param config The editor tool configuration
 * @param current The current editor panel item
 */
export const checkIsSelectable = (
  config: EditorTool['config'],
  current: EditorPanelItem,
  inspectionItems: InspectionItem[],
): boolean => {
  // If this belongs to a drawing, it is always selectable
  if (current.item && isDrawing(current.item)) {
    return true
  }

  // If it has onSelect callback defined, it is selectable
  if (current.item && current.onSelect) {
    return true
  }

  // inspection item type whitelist, but with custom defined item type, usually defined for non-inspection items
  if (current.itemType) {
    return config?.selectableInspectionItems?.includes(current.itemType) || false
  }

  // can be selected through inspection item whitelist
  if (current.item instanceof Object && isInspectionItem(current.item)) {
    return config?.selectableInspectionItems?.includes(current.item.item_type!) || false
  }

  // shape whitelist, but with custom defined shape type, usually defined for non-shape items
  if (current.shapeKey) {
    return config?.selectableShapes?.includes(current.shapeKey) || false
  }

  // can be selected through shape whitelist throught actual shape object
  if (current.item instanceof Object && isShape(current.item)) {
    const shapeType = deriveShapeType(current.item)
    if (shapeType && config?.selectableShapes?.includes(shapeType)) {
      return true
    }

    // through inspection item whitelist of the shape
    const items = inspectionItems.filter(
      (it) =>
        current.item instanceof Object &&
        isShape(current.item) &&
        (it.shape_ids.polygons?.includes(current.item.shape_id) ||
          it.shape_ids.cylinders?.includes(current.item.shape_id)),
    )
    if (items.length === 0) return false

    return items.some((item) => (item?.item_type ? config?.selectableInspectionItems?.includes(item.item_type) : false))
  }

  return false
}

/**
 * Get the current and its descendant IDs
 *
 * @param item The item
 * @returns
 */
export const getCurrentAndDescendantIds = (
  item: EditorPanelItem,
  forGroup: boolean,
  inspectionItems?: InspectionItem[],
  config?: EditorTool['config'],
): string[] => {
  const ids: string[] = []
  const isSelectable = checkIsSelectable(config, item, inspectionItems || [])

  if (!item.isVirtualContainer && (!config || (config && isSelectable))) {
    ids.push(getId(item))
  }

  if (!item.children) return ids

  return ids.concat(
    item.children
      .filter((child) => (!child.isGrouped && !forGroup) || forGroup)
      .map((child) => getCurrentAndDescendantIds(child, forGroup, inspectionItems, config))
      .flat(),
  )
}

/**
 * Get the items by the provided IDs.
 *
 * The difference between this function and the `collectItemsByIds` function is that this function
 * will return items in a flat array while the `collectItemsByIds` function will maintain the hierarchy.
 *
 * @param ids
 * @param items
 */
export const getItemsByIds = (ids: string[], items: EditorPanelItem[]): EditorPanelItem[] => {
  const foundItems: EditorPanelItem[] = []
  items.forEach((it) => {
    if (ids.includes(getId(it))) {
      foundItems.push(it)
    }

    if (it.children) {
      foundItems.push(...getItemsByIds(ids, it.children))
    }
  })

  return foundItems
}

/**
 * Get the items by the provided keys.
 * This is similar to `getItemsByIds`, but it uses the `key` property directly instead of
 * what is considered as the ID (through `getId` function).
 */
export const getItemsByKeys = (keys: string[], items: EditorPanelItem[]): EditorPanelItem[] => {
  const foundItems: EditorPanelItem[] = []
  items.forEach((it) => {
    if (keys.includes(it.key)) {
      foundItems.push(it)
    }

    if (it.children) {
      foundItems.push(...getItemsByKeys(keys, it.children))
    }
  })

  return foundItems
}
/**
 * Collect the item by the provided IDs.
 * - Recursively traverses children to match IDs.
 * - Matches reduce the remaining ID list to avoid duplicate matches.
 * - Preserves hierarchy.
 *
 * @param selectedIds Set of selected IDs, will be mutated as matches are found
 * @param it The item to check
 * @returns The item if matched or has matched children, otherwise null
 */
export const collectItemByIds = (selectedIds: Set<string>, it: EditorPanelItem): EditorPanelItem | null => {
  // If no IDs left to match, return null
  const id = getId(it)

  // Direct match
  if (selectedIds.has(id)) {
    const allIdsToRemove = getCurrentAndDescendantIds(it, true)
    allIdsToRemove.forEach((i) => selectedIds.delete(i))
    return it
  }

  // Check children recursively
  if (it.children?.length) {
    const collectedChildren = it.children
      .map((child) => collectItemByIds(selectedIds, child))
      .filter(Boolean) as EditorPanelItem[]

    if (collectedChildren.length) {
      return {
        ...it,
        children: collectedChildren,
      }
    }
  }

  return null
}

/**
 * Collect items by ID, maintaining hierarchy.
 * Mutates the ID set to prevent duplicate matches.
 *
 * @param ids List of target IDs
 * @param its Root items to check
 */
export const collectItemsByIds = (ids: string[], its: EditorPanelItem[]): EditorPanelItem[] => {
  const idSet = new Set(ids)
  const collected: EditorPanelItem[] = []

  for (let i = 0; i < its.length; i += 1) {
    const item = collectItemByIds(idSet, its[i])
    if (item) {
      collected.push(item)
    }

    if (idSet.size === 0) break // Optimization: stop early if all matched
  }

  return collected
}

/**
 * Find the first ancestor of the item with the given ID for which the callback returns true.
 *
 * @param items The list of editor panel items to search through.
 * @param targetId The ID of the item to find the parent for.
 * @param callback The function that checks the condition for the parent item.
 */
export const findParentByCondition = (
  root: EditorPanelItem[],
  targetId: string,
  callback: (node: EditorPanelItem) => boolean,
): EditorPanelItem | null => {
  const parentsMap = new Map<EditorPanelItem, EditorPanelItem>()

  const dfs = (node: EditorPanelItem, parent: EditorPanelItem | null): EditorPanelItem | null => {
    if (getId(node) === targetId) {
      let current: EditorPanelItem | null = parent
      while (current) {
        if (callback(current)) return current
        current = parentsMap.get(current) || null
      }
      return null
    }

    return (
      (node.children || [])
        .map((child) => {
          parentsMap.set(child, node)
          return dfs(child, node)
        })
        .find((res) => res != null) || null
    )
  }

  return root.map((node) => dfs(node, null)).find((res) => res != null) || null
}
/**
 * Cleans the editor panel item by removing function callbacks.
 * This is useful for serialization or when you want to store the item into Redux.
 *
 * @param item The editor panel item to be cleaned
 */
export const cleanEditorPanelItem = (item: EditorPanelItem): EditorPanelItem =>
  omit(item, ['onDelete', 'onReset', 'onSelect', 'onHover', 'children'])

/**
 * Find items within the provided group ID.
 *
 * @param groupId The group ID of the items.
 * @param items The items to search through.
 */
export const findItemsByGroupId = (groupId: string, items: EditorPanelItem[]): EditorPanelItem[] => {
  const foundItems: EditorPanelItem[] = []

  items.forEach((it) => {
    if (it.groupId === groupId) {
      foundItems.push(it)
    }

    if (it.item && 'inspection_group_id' in it.item && it.item.inspection_group_id === groupId) {
      foundItems.push(it)
    }

    if (it.children) {
      foundItems.push(...findItemsByGroupId(groupId, it.children))
    }
  })

  return foundItems
}

/**
 * Checks if an element (or layer, as it is defined for use in the editor's element panel) is hovered.
 * This will also return true if the group of the element is hovered, as the element is part of the group.
 *
 * @param elementId The element to check if it is hovered. It must implement the `InspectionGroupable` interface.
 * @param hoveredShapeId The ID of the currently hovered shape.
 * @param groups
 * @returns
 */
export const isElementHovered = (
  element: EditorPanelItem['item'],
  hoveredShapeId: string,
  groups: InspectionGroup[],
) => {
  if (getElementId(element) === hoveredShapeId) {
    return true
  }

  if (element instanceof Object && 'inspection_group_id' in element) {
    const group = groups.find((g) => g.group_id === (element as InspectionGroupable).inspection_group_id)
    if (group?.group_id === hoveredShapeId) {
      return true
    }
  }

  return false
}

/**
 * Check if the current element is selected.
 *
 * @param current The current editor panel item to check.
 * @param selectedElementIds The list of currently selected element IDs.
 * @param inspectionItems The list of inspection items to check against.
 * @param forGroup Whether to check for group selection.
 * @param currentEditorTool The current editor tool configuration, if any.
 */
export const isElementSelected = (
  current: EditorPanelItem,
  selectedElementIds: string[],
  inspectionItems: InspectionItem[],
  forGroup: boolean,
  currentEditorTool?: EditorTool,
) => {
  const { onSelect = true } = current
  const isShapeSelectable = !currentEditorTool || checkIsSelectable(currentEditorTool.config, current, inspectionItems)

  if (!onSelect || !selectedElementIds.length || !isShapeSelectable) return false

  // Nothing to select
  const ids = getCurrentAndDescendantIds(current, forGroup, inspectionItems, currentEditorTool?.config)
  if (!ids.length) return false

  return !difference(ids, selectedElementIds).length
}
